import React, { useState } from 'react';
import axios from 'axios';
import { Truck, PencilSimple } from '@phosphor-icons/react';
import { notify } from '../lib/notify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Won order: actual freight cost vs transport charged (VP / President / Sales Coordinator).
export default function FreightCostPanel({ quotation, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(quotation.actual_freight_cost ?? '');
  const [saving, setSaving] = useState(false);
  const charged = Number(quotation.total_transport || 0);
  const actual = quotation.actual_freight_cost;
  const diff = actual == null ? null : charged - Number(actual);
  const history = (quotation.freight_cost_history || []).slice().reverse();
  const [showAll, setShowAll] = useState(false);
  const shownHistory = showAll ? history : history.slice(0, 3);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/quotations/${quotation.id}/freight-cost`, { actual_freight_cost: value === '' ? null : Number(value) });
      notify('Freight cost saved', 'success');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      notify(e.response?.data?.detail || 'Could not save freight cost', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-3 p-3 rounded-lg border border-[#D5E8E4] bg-[#F4F9F7]" data-testid="freight-cost-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#052A23]"><Truck size={16} /> Freight</div>
        {!editing && (
          <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#00a69c] hover:underline" data-testid="freight-edit-btn">
            <PencilSimple size={12} /> {actual == null ? 'Enter actual cost' : 'Edit'}
          </button>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-2 text-xs">
        <div><p className="text-[#6D8E87]">Transport charged</p><p className="font-semibold" data-testid="freight-charged">{inr(charged)}</p></div>
        <div><p className="text-[#6D8E87]">Actual freight cost</p>
          <p className="font-semibold" data-testid="freight-actual">{actual == null ? <span className="text-amber-700">Not entered — using charged</span> : inr(actual)}</p></div>
        <div><p className="text-[#6D8E87]">Transport profit / loss</p>
          <p className={`font-semibold ${diff == null ? 'text-[#6D8E87]' : diff >= 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="freight-diff">{diff == null ? '₹0 (estimated)' : `${diff >= 0 ? '+' : '−'}${inr(Math.abs(diff))}`}</p></div>
      </div>
      {editing && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <input type="number" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Actual freight cost (₹) — blank to use charged"
            className="flex-1 min-w-[220px] px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm" data-testid="freight-cost-input" />
          <button type="button" onClick={save} disabled={saving} className="px-3 py-2 rounded-lg bg-[#00a69c] text-white text-xs font-semibold disabled:opacity-60" data-testid="freight-save-btn">{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" onClick={() => { setEditing(false); setValue(actual ?? ''); }} className="px-3 py-2 rounded-lg border border-[#D5E8E4] text-xs" data-testid="freight-cancel-btn">Cancel</button>
        </div>
      )}
      {history.length > 0 && (
        <ul className="mt-3 space-y-1 text-[11px] text-[#3B5A54]" data-testid="freight-history">
          {shownHistory.map((h, i) => (
            <li key={i}>{new Date(h.at).toLocaleString('en-IN')} · {h.by_name} ({h.by_role}): {h.before == null ? 'charged' : inr(h.before)} → {h.after == null ? 'charged' : inr(h.after)}</li>
          ))}
          {history.length > 3 && (
            <li>
              <button type="button" onClick={() => setShowAll(!showAll)} className="text-[#00a69c] font-semibold hover:underline" data-testid="freight-history-toggle">
                {showAll ? 'Show less' : `Show all ${history.length} changes`}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
